import "./index.scss";

import { FormattedMessage } from "react-intl";

import {
  MeetingEndedState,
  CompletionRequirement,
  ParticipantTypes,
  OrgTransactionStates,
} from "graphql_globals";
import CollapsibleList from "common/core/collapsible_list";
import modalScrollContent from "common/core/modal_scroll_content";
import { usePermissions } from "common/core/current_user_role";
import { dateComparator } from "util/date";
import { AVAILABLE_FEATURES } from "constants/organization";
import { RECIPIENT_ORDER } from "constants/feature_gates";
import { useFeatureFlag } from "common/feature_gating";

import CustomerSignerDetails from "./customer_signer_details";
import SignerDetails from "./signer_details";
import type {
  DocumentBundleForTransactionDetailsSigner,
  DocumentBundleForTransactionDetailsSigner_transaction_customerSigners as CustomerSigner,
} from "./index_fragment.graphql";
import SignerV2 from "./index_v2";

type Props = {
  bundle: DocumentBundleForTransactionDetailsSigner;
  meetingIds?: string[];
  refetch: () => Promise<unknown>;
};
type CorrespondingCS = Pick<
  CustomerSigner,
  | "id"
  | "transactionAccessLink"
  | "phone"
  | "firstName"
  | "middleName"
  | "lastName"
  | "alternativeNames"
  | "proofRequirement"
>;
type SIListItem = { id: string; participantType: ParticipantTypes; user: { id: string } };

function Signer({ bundle, meetingIds, refetch }: Props) {
  const { hasPermissionFor } = usePermissions();
  const {
    state,
    publicOrganization,
    customerSigners: originalCustomerSigners,
    isMortgage,
  } = bundle.transaction || {};
  const execRefetch = () => refetch();
  const organizationFeatures =
    publicOrganization?.featureFlags.filter((f) => f.value === "true").map((f) => f.key) || [];
  if (isMortgage) {
    organizationFeatures.push(AVAILABLE_FEATURES.SHOW_PICTURE_IDS);
  }
  const organizationTierFeatures = publicOrganization?.featureList || [];
  const customerSigners = originalCustomerSigners || [];

  // An object that maps user IDs to a transaction customer (if it exists)
  const correspondingCustomerSigners: Record<string, Partial<CorrespondingCS>> = {};
  bundle.participants!.forEach((participant) => {
    const correspondingCustomerSigner = customerSigners.find(
      (customerSigner) => participant!.signerRole.index === customerSigner.signerRole.index,
    );
    // There will be no correspondingCustomerSigner if the bundle is retail or something from easylink
    correspondingCustomerSigners[participant!.userId] = {
      id: correspondingCustomerSigner?.id,
      transactionAccessLink: correspondingCustomerSigner?.transactionAccessLink,
      phone: correspondingCustomerSigner?.phone,
      firstName: participant!.firstName,
      middleName: participant!.middleName,
      lastName: participant!.lastName,
      alternativeNames: correspondingCustomerSigner?.alternativeNames,
      proofRequirement: correspondingCustomerSigner?.proofRequirement,
    };
  });

  // Always default to notarization as completion requirement
  const completionRequirements = bundle.completionRequirements || [
    CompletionRequirement.NOTARIZATION,
  ];

  /*
    The whole admin portal code should be nuked.
    This Signer component is used in multiple places, with different contexts, where the
    root nodes might be a Meeting or an OrganizationTransaction. Depending on the context,
    with want to show all meetings, some meetings, or only 1 meeting.
    We use the `meetingIds` array as a hack when the root node is a Meeting and we only want this one.
   */
  const filteredMeetings = bundle.meetings.edges
    .filter(({ node: meeting }) => {
      if (meetingIds !== undefined) {
        return meetingIds.includes(meeting.id);
      } else if (hasPermissionFor("filterTerminatedMeetings")) {
        return meeting.endedState === MeetingEndedState.COMPLETED;
      }
      return true;
    })
    .map((edge) => edge.node)
    .sort((a, b) => dateComparator(b.timeFrame!.startedAt, a.timeFrame!.startedAt));
  const reversedFilteredMeetings = filteredMeetings.reverse();

  // Classifies participants by their type and makes sure they are unique
  const signerIdentitiesList: SIListItem[] = [];
  const credibleWitnessList: SIListItem[] = [];
  const uniqueSignerList: string[] = [];
  for (const { participants } of filteredMeetings) {
    for (const participant of participants!) {
      if (
        participant!.participantType === ParticipantTypes.SIGNER &&
        !signerIdentitiesList.includes(participant as SIListItem)
      ) {
        signerIdentitiesList.push(participant as SIListItem);
        if (!uniqueSignerList.includes(participant!.user!.id)) {
          uniqueSignerList.push(participant!.user!.id);
        }
      }
      if (
        participant!.participantType === ParticipantTypes.CREDIBLE_WITNESS &&
        !credibleWitnessList.includes(participant as SIListItem)
      ) {
        credibleWitnessList.push(participant as SIListItem);
      }
    }
  }

  const signers =
    state === OrgTransactionStates.STARTED ||
    state === OrgTransactionStates.SENT_TO_CLOSING_OPS ||
    state === OrgTransactionStates.SENT_TO_TITLE_AGENCY ? (
      <>
        {customerSigners.map((signer, i) => (
          <CustomerSignerDetails
            key={signer.id}
            participant={bundle.participants?.find(
              (p) => p?.signerRole.index === signer.signerRole.index,
            )}
            customerSigner={signer}
            subheader={
              <FormattedMessage
                id="e7547f39-78d9-442f-a1fc-d621cf447d86"
                defaultMessage="Signer {index}"
                values={{ index: i + 1 }}
              />
            }
          />
        ))}
      </>
    ) : (
      <>
        {signerIdentitiesList.map((signerIdentity) => (
          <SignerDetails
            key={signerIdentity.id}
            completionRequirements={completionRequirements}
            organizationFeatures={organizationFeatures}
            organizationTierFeatures={organizationTierFeatures}
            bundleSignerInfo={signerIdentity.user}
            signerIdentityId={signerIdentity.id}
            meetingsInfo={reversedFilteredMeetings}
            subheader={
              <FormattedMessage
                id="9be5a37f-b6cd-47db-944e-44731f5a2fb8"
                defaultMessage="Signer {index}"
                values={{ index: uniqueSignerList.indexOf(signerIdentity.user.id) + 1 }}
              />
            }
            participantType={signerIdentity.participantType}
            validatedByCredibleWitness={credibleWitnessList.length > 0}
            transactionCustomer={
              correspondingCustomerSigners[signerIdentity.user.id] as CorrespondingCS
            }
            onUpdate={execRefetch}
          />
        ))}

        {credibleWitnessList.map((credibleWitness) => (
          <SignerDetails
            key={credibleWitness.id}
            completionRequirements={completionRequirements}
            organizationFeatures={organizationFeatures}
            organizationTierFeatures={organizationTierFeatures}
            bundleSignerInfo={credibleWitness.user}
            signerIdentityId={credibleWitness.id}
            meetingsInfo={reversedFilteredMeetings}
            subheader={
              <FormattedMessage
                id="3c0349a1-2e36-4b07-9766-8f5cbcd934de"
                defaultMessage="Credible Witness"
              />
            }
            participantType={credibleWitness.participantType}
            onUpdate={execRefetch}
          />
        ))}

        {/* For signers that don't exist in the list of signer identities, we should still display them */}
        {bundle.signers!.map((signer, i) => {
          if (
            !signer ||
            signerIdentitiesList.some((signerIdentity) => signerIdentity.user.id === signer.id)
          ) {
            return null;
          }
          return (
            // for when there are no meetings yet
            <SignerDetails
              key={signer.id}
              completionRequirements={completionRequirements}
              organizationFeatures={organizationFeatures}
              organizationTierFeatures={organizationTierFeatures}
              bundleSignerInfo={signer}
              subheader={
                <FormattedMessage
                  id="b574ed3f-25e3-4f36-a89d-dd5ced81f34e"
                  defaultMessage="Signer {index}"
                  values={{ index: i + 1 }}
                />
              }
              transactionCustomer={correspondingCustomerSigners[signer.id] as CorrespondingCS}
              onUpdate={execRefetch}
              authenticationRequirement={bundle.transaction?.authenticationRequirement}
              esignAuthSignerIdentities={bundle.transaction?.signerIdentities}
            />
          );
        })}
      </>
    );

  return (
    <div className="NotarizationDetails-user">
      <CollapsibleList>{signers}</CollapsibleList>
    </div>
  );
}

function SignerContainer(props: Props) {
  const recipientOrderEnabled = useFeatureFlag(RECIPIENT_ORDER);

  return recipientOrderEnabled ? <SignerV2 {...props} /> : <Signer {...props} />;
}

export default modalScrollContent(SignerContainer);
